import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  DraggableProvidedDraggableProps,
  DropResult,
  Droppable,
} from '@hello-pangea/dnd';
import { ButtonColor, ButtonVariant, Icon, IconButton, TextButton, TextInput } from '../../components';
import { ScorecardQuestionType, ScorecardSectionType } from '../../types';
import { getNewScorecardQuestion } from '../../utils';
import ScorecardTemplateQuestion from './ScorecardTemplateQuestion';

interface ScorecardTemplateSectionProps {
  section: ScorecardSectionType;
  isAIScorecard: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  draggableProps?: DraggableProvidedDraggableProps;
  innerRef?: React.Ref<HTMLDivElement>;
  onUpdate?: (updatedSection: ScorecardSectionType) => void;
  onRemove?: () => void;
}

const ScorecardTemplateSection = ({
  section,
  isAIScorecard,
  onUpdate,
  onRemove,
  dragHandleProps,
  draggableProps,
  innerRef,
}: ScorecardTemplateSectionProps) => {
  // Add a question to the section
  const handleAddQuestion = () => {
    const newQuestion = getNewScorecardQuestion(isAIScorecard);
    const updatedQuestions = [...section.questions, newQuestion];
    onUpdate?.({ ...section, questions: updatedQuestions });
  };

  // Update a question in the section
  const handleUpdateQuestion = (index: number, updatedQuestion: ScorecardQuestionType) => {
    const updatedQuestions = [...section.questions];
    updatedQuestions[index] = updatedQuestion;
    onUpdate?.({ ...section, questions: updatedQuestions });
  };

  // Remove a question from the section
  const handleRemoveQuestion = (index: number) => {
    const updatedQuestions = section.questions.filter((_, i) => i !== index);
    onUpdate?.({ ...section, questions: updatedQuestions });
  };

  // Handle drag end event
  const handleQuestionDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reorderedQuestions = Array.from(section.questions);
    const [movedQuestion] = reorderedQuestions.splice(result.source.index, 1);
    reorderedQuestions.splice(result.destination.index, 0, movedQuestion);

    onUpdate?.({ ...section, questions: reorderedQuestions });
  };

  return (
    <div ref={innerRef} {...draggableProps}>
      <DragDropContext onDragEnd={handleQuestionDragEnd}>
        <div className="flex items-start gap-2">
          <div {...dragHandleProps}>
            <IconButton icon={Icon.GRIP_VERTICAL} color={ButtonColor.NEUTRAL} variant={ButtonVariant.GHOST} />
          </div>
          <div className="flex w-full flex-col gap-2">
            <TextInput
              placeholder="Section name*"
              onChange={(e) => onUpdate?.({ ...section, title: e.target.value })}
              value={section.title}
            />
            <div className="flex flex-col gap-4 p-2">
              {section.questions?.length > 0 && (
                <Droppable
                  droppableId={section.id}
                  renderClone={(provided, _snapshot, rubric) => (
                    <ScorecardTemplateQuestion
                      question={section.questions[rubric.source.index]}
                      isAIScorecard={isAIScorecard}
                      dragHandleProps={provided.dragHandleProps || undefined}
                      draggableProps={provided.draggableProps || undefined}
                      innerRef={provided.innerRef}
                    />
                  )}
                >
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      className="flex flex-col gap-4"
                    >
                      {section.questions?.map((question, index) => (
                        <Draggable draggableId={question.id} index={index} key={question.id}>
                          {(draggableProvided) => (
                            <ScorecardTemplateQuestion
                              question={question}
                              isAIScorecard={isAIScorecard}
                              dragHandleProps={draggableProvided.dragHandleProps || undefined}
                              draggableProps={draggableProvided.draggableProps || undefined}
                              innerRef={draggableProvided.innerRef}
                              onUpdate={(updatedQuestion) => handleUpdateQuestion(index, updatedQuestion)}
                              onRemove={() => handleRemoveQuestion(index)}
                            />
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              )}
              <TextButton
                text="Add question"
                startIcon={Icon.PLUS}
                onClick={handleAddQuestion}
                variant={ButtonVariant.SOFT}
              />
            </div>
          </div>
          <IconButton
            icon={Icon.TRASH}
            onClick={onRemove}
            color={ButtonColor.DESTRUCTIVE}
            variant={ButtonVariant.GHOST}
          />
        </div>
      </DragDropContext>
    </div>
  );
};

export default ScorecardTemplateSection;
